// src/pages/AdminHome.jsx
// نسخه‌ی نهایی نهایی — کاملاً واقعی، بدون داده‌ی فیک
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import moment from 'moment-jalaali';
import './AdminHome.css';
import { getToken } from '../utils/secureStorage';
import { getGrade, getField } from '../core/education/curriculum.js';

// ────────────────────────────────────────────────────────────────
//  Helpers
// ────────────────────────────────────────────────────────────────
const faNum = (n) => Number(n || 0).toLocaleString('fa-IR');

const sanitizeUser = (u) => ({
  id: u.id,
  role: u.role,
  firstName: u.firstName || '',
  lastName: u.lastName || '',
  fullName: u.fullName || u.name || '',
  phone: u.phone || '',
  grade: u.grade || '',
  field: u.field || '',
  coins: Number(u.coins) || 0,
  xp: Number(u.xp) || 0,
  createdAt: u.createdAt || '',
  active: u.active !== false,
  'firstName-student': u['firstName-student'] || '',
  'lastName-student': u['lastName-student'] || '',
  'phone-student': u['phone-student'] || '',
  'grade-student': u['grade-student'] || '',
  'field-student': u['field-student'] || '',
});

// ────────────────────────────────────────────────────────────────
//  Data Hook — تمام داده‌ی واقعی از سرور
// ────────────────────────────────────────────────────────────────
const useAdminData = () => {
  const [state, setState] = useState({
    users: [],
    exams: [],
    uploads: [],
    teams: [],
    loading: true,
    error: null,
    lastUpdated: null,
  });

  const refresh = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const token = getToken();
      if (!token) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: 'جلسه منقضی شده — لطفاً دوباره وارد شوید',
        }));
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const [usersR, examsR, uploadsR, teamsR] = await Promise.allSettled([
        fetch('/api/db/users', { headers }).then((r) => (r.ok ? r.json() : [])).catch(() => []),
        fetch('/api/db/exams', { headers }).then((r) => (r.ok ? r.json() : [])).catch(() => []),
        fetch('/api/db/adminUploads', { headers }).then((r) => (r.ok ? r.json() : [])).catch(() => []),
        fetch('/api/db/teams', { headers }).then((r) => (r.ok ? r.json() : [])).catch(() => []),
      ]);

      const safeArr = (r, mapper = (x) => x) =>
        r.status === 'fulfilled' && Array.isArray(r.value)
          ? r.value.filter((x) => x && typeof x === 'object').map(mapper)
          : [];

      const usersArr = safeArr(usersR, sanitizeUser);
      const examsArr = safeArr(examsR);
      const uploadsArr = safeArr(uploadsR);
      const teamsArr = safeArr(teamsR);

      const allEmpty =
        usersArr.length === 0 &&
        examsArr.length === 0 &&
        uploadsArr.length === 0 &&
        teamsArr.length === 0;

      setState({
        users: usersArr,
        exams: examsArr,
        uploads: uploadsArr,
        teams: teamsArr,
        loading: false,
        error: allEmpty ? 'خطا در دریافت داده از سرور' : null,
        lastUpdated: new Date(),
      });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'خطا در اتصال به سرور',
      }));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { ...state, refresh };
};

// ────────────────────────────────────────────────────────────────
//  AdminHome wrapper (unchanged)
// ────────────────────────────────────────────────────────────────
const AdminHome = () => {
  const [isDark] = useState(false);

  return (
    <div
      className={`admin-home ${isDark ? 'dark-mode' : ''}`}
      style={{
        minHeight: '100vh',
        backgroundImage: isDark ? "url('/h-dark.jpg')" : "url('/h.jpg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
      }}
    >
      <div style={{ height: '85px' }} />
      <div className="app-container">
        <Header isDark={isDark} setIsDark={() => {}} />
        <main className="main-content">
          <AdminDashboard />
        </main>
      </div>
      <div style={{ height: '85px' }} />
    </div>
  );
};

// Header — بدون تغییر (خارج از scope)
const Header = () => {
  return null;
};

// ────────────────────────────────────────────────────────────────
//  Presentational atoms
// ────────────────────────────────────────────────────────────────
const Card = ({ title, badge, badgeColor, icon, iconColor, children }) => (
  <div className="card-modern">
    <div className="card-header">
      <div className="card-header-left">
        <i className={`fas ${icon}`} style={iconColor ? { color: iconColor } : {}}></i>
        <span>{title}</span>
      </div>
      {badge && <span className={`badge badge-${badgeColor}`}>{badge}</span>}
    </div>
    <div className="card-body">{children}</div>
  </div>
);

const StatWidget = ({ icon, value, label, color }) => (
  <div className="stat-widget">
    <div className={`stat-icon ${color}`}>
      <i className={`fas ${icon}`}></i>
    </div>
    <div className="stat-info">
      <div className="stat-number">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  </div>
);

const ServiceStatus = ({ name, ok }) => (
  <div className="service-status">
    <span>
      <span
        className="activity-dot"
        style={{ background: ok ? 'var(--success)' : 'var(--danger)' }}
      ></span>{' '}
      {name}
    </span>
    <span
      style={{
        color: ok ? 'var(--success)' : 'var(--danger)',
        fontSize: '0.7rem',
        fontWeight: 700,
      }}
    >
      {ok ? '✅ فعال' : '❌ خطا'}
    </span>
  </div>
);

// ────────────────────────────────────────────────────────────────
//  Registration Chart — تب روزانه/ماهانه + داده‌ی واقعی
// ────────────────────────────────────────────────────────────────
const RegistrationChart = ({ users }) => {
  const [mode, setMode] = useState('daily');

  const chartData = useMemo(() => {
    const days = mode === 'daily' ? 7 : 30;
    const today = moment().startOf('day');
    const buckets = [];

    for (let i = days - 1; i >= 0; i--) {
      const day = moment(today).subtract(i, 'days');
      buckets.push({ key: day.valueOf(), date: day.clone(), count: 0 });
    }

    const bucketMap = new Map(buckets.map((b) => [b.key, b]));

    for (const u of users) {
      if (!u.createdAt) continue;
      const d = moment(u.createdAt);
      if (!d.isValid()) continue;
      const k = d.startOf('day').valueOf();
      const bucket = bucketMap.get(k);
      if (bucket) bucket.count += 1;
    }

    return buckets;
  }, [users, mode]);

  const maxCount = useMemo(
    () => Math.max(1, ...chartData.map((d) => d.count)),
    [chartData]
  );

  const total = useMemo(
    () => chartData.reduce((s, d) => s + d.count, 0),
    [chartData]
  );

  const peak = useMemo(
    () =>
      chartData.reduce(
        (best, d) => (d.count > best.count ? d : best),
        { count: 0, date: null }
      ),
    [chartData]
  );

  const DAYS_SHORT = ['ی', 'د', 'س', 'چ', 'پ', 'ج', 'ش'];

  const tabStyle = (active) => ({
    padding: '5px 14px',
    borderRadius: '16px',
    fontSize: '0.68rem',
    fontWeight: 700,
    border: 'none',
    cursor: 'pointer',
    background: active ? 'var(--primary)' : 'var(--border-color)',
    color: active ? '#fff' : 'var(--text-secondary)',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
  });

  return (
    <Card
      title="ثبت‌نام"
      badge={mode === 'daily' ? '۷ روز' : '۳۰ روز'}
      badgeColor="primary"
      icon="fa-chart-bar"
    >
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
        <button type="button" style={tabStyle(mode === 'daily')} onClick={() => setMode('daily')}>
          روزانه
        </button>
        <button type="button" style={tabStyle(mode === 'monthly')} onClick={() => setMode('monthly')}>
          ماهانه
        </button>
      </div>

      {/* Bars */}
      <div className="chart-bars" style={{ height: '80px', gap: mode === 'daily' ? '6px' : '2px' }}>
        {chartData.map((d, i) => {
          const h = d.count === 0 ? 4 : Math.round((d.count / maxCount) * 60) + 10;
          return (
            <div
              key={i}
              className="bar"
              style={{
                height: `${h}px`,
                opacity: d.count === 0 ? 0.25 : 0.85,
              }}
              title={`${d.date.format('jD jMMMM')}: ${faNum(d.count)}`}
            >
              {d.count > 0 && (
                <span className="bar-value" style={{ fontSize: '0.5rem' }}>
                  {faNum(d.count)}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Labels */}
      <div className="chart-labels">
        {chartData.map((d, i) => {
          if (mode === 'daily') {
            return <span key={i}>{DAYS_SHORT[d.date.day()]}</span>;
          }
          if (i % 5 === 0 || i === chartData.length - 1) {
            return (
              <span key={i} style={{ fontSize: '0.5rem' }}>
                {faNum(d.date.jDate())}
              </span>
            );
          }
          return <span key={i}></span>;
        })}
      </div>

      {/* Footer */}
      <div className="chart-trend" style={{ fontSize: '0.68rem' }}>
        {total === 0 ? (
          <span style={{ color: 'var(--text-muted)' }}>
            ثبت‌نامی در {mode === 'daily' ? '۷' : '۳۰'} روز گذشته نبوده
          </span>
        ) : (
          <>
            <i className="fas fa-check-circle" style={{ color: 'var(--success)' }}></i>{' '}
            <strong>{faNum(total)}</strong> ثبت‌نام
            {peak.count > 0 && peak.date && (
              <>
                {' • '}اوج: <strong>{faNum(peak.count)}</strong> در{' '}
                {peak.date.format('jD jMMMM')}
              </>
            )}
          </>
        )}
      </div>
    </Card>
  );
};

// ────────────────────────────────────────────────────────────────
//  Users Modal — دو مرحله‌ای
// ────────────────────────────────────────────────────────────────
const UsersModal = ({ isOpen, onClose, students, teachers, parents }) => {
  const [step, setStep] = useState('roles');
  const [role, setRole] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  // reset on close
  useEffect(() => {
    if (!isOpen) {
      setStep('roles');
      setRole(null);
      setSearch('');
      setSortBy('newest');
    }
  }, [isOpen]);

  // body scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (step === 'table') setStep('roles');
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, step, onClose]);

  const roleList = useMemo(
    () => [
      { id: 'student', label: 'دانش‌آموزان', icon: 'fa-user-graduate', data: students, color: 'var(--primary)' },
      { id: 'teacher', label: 'معلمان', icon: 'fa-chalkboard-teacher', data: teachers, color: 'var(--success)' },
      { id: 'parent', label: 'والدین', icon: 'fa-user-friends', data: parents, color: 'var(--warning)' },
    ],
    [students, teachers, parents]
  );

  const activeRole = roleList.find((r) => r.id === role);

  const columns = useMemo(() => {
    if (role === 'student')
      return [
        { key: 'name', label: 'نام کامل' },
        { key: 'grade', label: 'پایه' },
        { key: 'field', label: 'رشته' },
        { key: 'phone', label: 'موبایل' },
        { key: 'coins', label: 'سکه' },
        { key: 'xp', label: 'XP' },
        { key: 'createdAt', label: 'تاریخ ثبت‌نام' },
      ];
    if (role === 'teacher')
      return [
        { key: 'name', label: 'نام کامل' },
        { key: 'grade', label: 'پایه تدریس' },
        { key: 'field', label: 'رشته تدریس' },
        { key: 'phone', label: 'موبایل' },
        { key: 'coins', label: 'سکه' },
        { key: 'xp', label: 'XP' },
        { key: 'createdAt', label: 'تاریخ عضویت' },
      ];
    return [
      { key: 'name', label: 'نام والد' },
      { key: 'phone', label: 'موبایل والد' },
      { key: 'studentName', label: 'نام فرزند' },
      { key: 'studentGrade', label: 'پایه فرزند' },
      { key: 'studentField', label: 'رشته فرزند' },
      { key: 'studentPhone', label: 'موبایل فرزند' },
      { key: 'createdAt', label: 'تاریخ عضویت' },
    ];
  }, [role]);

  const filtered = useMemo(() => {
    if (!activeRole) return [];
    let list = [...activeRole.data];

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((u) => {
        const hay = [
          u.firstName,
          u.lastName,
          u.fullName,
          u.phone,
          u.grade,
          u.field,
          u['firstName-student'],
          u['lastName-student'],
          u['phone-student'],
          u['grade-student'],
          u['field-student'],
        ]
          .filter(Boolean)
          .map((x) => String(x).toLowerCase());
        return hay.some((h) => h.includes(q));
      });
    }

    if (sortBy === 'newest') {
      list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    } else if (sortBy === 'oldest') {
      list.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    } else if (sortBy === 'name') {
      list.sort((a, b) => {
        const aN =
          `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.fullName || '';
        const bN =
          `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.fullName || '';
        return aN.localeCompare(bN, 'fa');
      });
    }

    return list;
  }, [activeRole, search, sortBy]);

  const getCell = (u, key) => {
    switch (key) {
      case 'name':
        return (
          `${u.firstName || ''} ${u.lastName || ''}`.trim() ||
          u.fullName ||
          '—'
        );
      case 'grade':
        return getGrade(u.grade)?.title || u.grade || '—';
      case 'field':
        return u.field ? getField(u.field)?.title || u.field : '—';
      case 'phone':
        return u.phone || '—';
      case 'coins':
        return faNum(u.coins);
      case 'xp':
        return faNum(u.xp);
      case 'createdAt':
        return u.createdAt ? moment(u.createdAt).format('jYYYY/jMM/jDD') : '—';
      case 'studentName':
        return (
          `${u['firstName-student'] || ''} ${u['lastName-student'] || ''}`.trim() ||
          '—'
        );
      case 'studentGrade':
        return getGrade(u['grade-student'])?.title || u['grade-student'] || '—';
      case 'studentField':
        return u['field-student']
          ? getField(u['field-student'])?.title || u['field-student']
          : '—';
      case 'studentPhone':
        return u['phone-student'] || '—';
      default:
        return '—';
    }
  };

  if (!isOpen) return null;

  // ── Styles
  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    zIndex: 99999,
    background: 'rgba(0,0,0,0.65)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
  };

  const boxStyle = {
    background: 'var(--bg-card, #fff)',
    color: 'var(--text-primary, #1a1a2e)',
    borderRadius: '20px',
    width: '100%',
    maxWidth: '950px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
    border: '1px solid var(--border-color, #f0f2f7)',
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    borderBottom: '1px solid var(--border-color, #f0f2f7)',
    flexShrink: 0,
    gap: '10px',
    background: 'var(--bg-card, #fff)',
  };

  const iconBtn = {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    border: '1px solid var(--border-color, #f0f2f7)',
    background: 'transparent',
    color: 'var(--text-secondary, #475569)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.9rem',
    flexShrink: 0,
    fontFamily: 'inherit',
  };

  const cellStyle = {
    padding: '10px 12px',
    borderBottom: '1px solid var(--border-color, #f0f2f7)',
    textAlign: 'right',
    fontSize: '0.78rem',
    whiteSpace: 'nowrap',
    color: 'var(--text-primary, #1a1a2e)',
  };

  const headCellStyle = {
    ...cellStyle,
    fontWeight: 700,
    background: 'var(--bg-primary, #f5f7fb)',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    fontSize: '0.72rem',
    color: 'var(--text-secondary, #475569)',
  };

  const roleBtnStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    padding: '16px',
    borderRadius: '14px',
    border: '1.5px solid var(--border-color, #f0f2f7)',
    background: 'var(--bg-card, #fff)',
    color: 'var(--text-primary, #1a1a2e)',
    cursor: 'pointer',
    textAlign: 'right',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
    fontWeight: 600,
    transition: 'all 0.2s ease',
  };

  // ── Roles step
  const renderRolesStep = () => (
    <div
      style={{
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        overflowY: 'auto',
      }}
    >
      {roleList.map((r) => (
        <button
          key={r.id}
          type="button"
          style={roleBtnStyle}
          onClick={() => {
            setRole(r.id);
            setStep('table');
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--bg-primary, #f5f7fb)';
            e.currentTarget.style.borderColor = 'var(--primary, #4f46e5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'var(--bg-card, #fff)';
            e.currentTarget.style.borderColor = 'var(--border-color, #f0f2f7)';
          }}
        >
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '12px',
              background: 'var(--bg-primary, #f5f7fb)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: r.color,
              fontSize: '1.1rem',
              flexShrink: 0,
            }}
          >
            <i className={`fas ${r.icon}`}></i>
          </div>
          <span style={{ flex: 1 }}>{r.label}</span>
          <span
            style={{
              fontSize: '0.78rem',
              color: 'var(--text-muted, #94a3b8)',
              fontWeight: 500,
            }}
          >
            {faNum(r.data.length)} نفر
          </span>
          <i
            className="fas fa-chevron-left"
            style={{ color: 'var(--text-muted, #94a3b8)', fontSize: '0.8rem' }}
          ></i>
        </button>
      ))}
    </div>
  );

  // ── Table step
  const renderTableStep = () => (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          padding: '12px 18px',
          borderBottom: '1px solid var(--border-color, #f0f2f7)',
          flexWrap: 'wrap',
          flexShrink: 0,
        }}
      >
        <div style={{ flex: 1, minWidth: '160px', position: 'relative' }}>
          <input
            type="text"
            placeholder="جستجو در نام، موبایل، پایه..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 34px 8px 12px',
              borderRadius: '10px',
              border: '1.5px solid var(--border-color, #f0f2f7)',
              background: 'var(--bg-primary, #f5f7fb)',
              color: 'var(--text-primary, #1a1a2e)',
              fontSize: '0.78rem',
              fontFamily: 'inherit',
              outline: 'none',
            }}
          />
          <i
            className="fas fa-search"
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted, #94a3b8)',
              fontSize: '0.75rem',
              pointerEvents: 'none',
            }}
          ></i>
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{
            padding: '8px 12px',
            borderRadius: '10px',
            border: '1.5px solid var(--border-color, #f0f2f7)',
            background: 'var(--bg-primary, #f5f7fb)',
            color: 'var(--text-primary, #1a1a2e)',
            fontSize: '0.78rem',
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          <option value="newest">جدیدترین</option>
          <option value="oldest">قدیمی‌ترین</option>
          <option value="name">بر اساس الفبا</option>
        </select>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {filtered.length === 0 ? (
          <div
            style={{
              padding: '48px 20px',
              textAlign: 'center',
              color: 'var(--text-muted, #94a3b8)',
            }}
          >
            <i
              className="fas fa-inbox"
              style={{ fontSize: '2rem', marginBottom: '10px', display: 'block' }}
            ></i>
            <div style={{ fontSize: '0.85rem' }}>کاربری با این مشخصات یافت نشد</div>
          </div>
        ) : (
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              minWidth: '700px',
            }}
          >
            <thead>
              <tr>
                <th style={{ ...headCellStyle, width: '48px', textAlign: 'center' }}>#</th>
                {columns.map((c) => (
                  <th key={c.key} style={headCellStyle}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((u, i) => (
                <tr
                  key={u.id || i}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--bg-primary, #f5f7fb)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <td style={{ ...cellStyle, textAlign: 'center', color: 'var(--text-muted)', fontWeight: 600 }}>
                    {faNum(i + 1)}
                  </td>
                  {columns.map((c) => (
                    <td key={c.key} style={cellStyle}>
                      {getCell(u, c.key)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  return (
    <div
      style={overlayStyle}
      onClick={() => {
        if (step === 'table') setStep('roles');
        else onClose();
      }}
    >
      <div style={boxStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              minWidth: 0,
              flex: 1,
            }}
          >
            {step === 'table' && (
              <button
                type="button"
                style={iconBtn}
                onClick={() => setStep('roles')}
                title="بازگشت"
              >
                <i className="fas fa-arrow-right"></i>
              </button>
            )}
            <h3
              style={{
                margin: 0,
                fontSize: '0.95rem',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: 'var(--text-primary, #1a1a2e)',
              }}
            >
              {step === 'roles' ? (
                <>
                  <i
                    className="fas fa-users"
                    style={{ marginLeft: '6px', color: 'var(--primary, #4f46e5)' }}
                  ></i>{' '}
                  کاربران
                </>
              ) : (
                <>
                  <i
                    className={`fas ${activeRole?.icon}`}
                    style={{ marginLeft: '6px', color: activeRole?.color }}
                  ></i>{' '}
                  {activeRole?.label} — {faNum(filtered.length)} از{' '}
                  {faNum(activeRole?.data.length || 0)}
                </>
              )}
            </h3>
          </div>
          <button type="button" style={iconBtn} onClick={onClose} title="بستن">
            <i className="fas fa-times"></i>
          </button>
        </div>

        {step === 'roles' ? renderRolesStep() : renderTableStep()}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
//  Dashboard — داده‌ی واقعی، بدون فیک
// ────────────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const { users, exams, uploads, teams, loading, error, lastUpdated, refresh } =
    useAdminData();

  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }, []);

  const students = useMemo(() => users.filter((u) => u.role === 'student'), [users]);
  const teachers = useMemo(() => users.filter((u) => u.role === 'teacher'), [users]);
  const parents = useMemo(() => users.filter((u) => u.role === 'parent'), [users]);

  const lastUpdateStr = lastUpdated ? moment(lastUpdated).format('HH:mm') : '—';

  const adminItems = [
    { icon: 'fa-users-cog', label: 'کاربران', action: () => setIsUsersModalOpen(true), active: true },
    { icon: 'fa-book-open', label: 'محتوا', action: () => showToast('🚧 به‌زودی...'), active: false },
    { icon: 'fa-robot', label: 'AI', action: () => showToast('🚧 به‌زودی...'), active: false },
    { icon: 'fa-database', label: 'پشتیبان', action: () => showToast('🚧 به‌زودی...'), active: false },
    { icon: 'fa-shield-alt', label: 'امنیت', action: () => showToast('🚧 به‌زودی...'), active: false },
    { icon: 'fa-file-alt', label: 'گزارش‌ها', action: () => showToast('🚧 به‌زودی...'), active: false },
  ];

  // ── Loading
  if (loading) {
    return (
      <div className="role-section active" style={{ textAlign: 'center', padding: '60px 20px' }}>
        <i
          className="fas fa-spinner fa-spin"
          style={{ fontSize: '2rem', color: 'var(--primary)' }}
        ></i>
        <div style={{ marginTop: '12px', color: 'var(--text-muted)' }}>
          در حال بارگذاری داده‌های واقعی...
        </div>
      </div>
    );
  }

  // ── Error
  if (error) {
    return (
      <div className="role-section active">
        <div
          style={{
            padding: '28px',
            textAlign: 'center',
            background: 'var(--bg-card)',
            borderRadius: '20px',
            border: '1.5px solid var(--danger)',
            marginTop: '20px',
          }}
        >
          <i
            className="fas fa-exclamation-triangle"
            style={{
              fontSize: '2rem',
              color: 'var(--danger)',
              marginBottom: '10px',
              display: 'block',
            }}
          ></i>
          <div
            style={{
              fontSize: '0.95rem',
              fontWeight: 700,
              color: 'var(--text-primary)',
              marginBottom: '6px',
            }}
          >
            خطا در بارگذاری داده
          </div>
          <div
            style={{
              fontSize: '0.8rem',
              color: 'var(--text-muted)',
              marginBottom: '18px',
            }}
          >
            {error}
          </div>
          <button type="button" className="btn-primary btn-sm" onClick={refresh}>
            <i className="fas fa-redo"></i> تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  // ── Success
  return (
    <>
      <div className="role-section admin-section active">
        {/* Greeting */}
        <div className="greeting-banner admin-greeting">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="greeting-title">📊 پنل مدیریت</div>
            <div className="greeting-sub">
              داده‌ی زنده • آخرین به‌روزرسانی: {lastUpdateStr}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div
              className="greeting-badge"
              style={{ background: 'var(--info)', whiteSpace: 'nowrap' }}
            >
              کل: {faNum(users.length)}
            </div>
            <button
              type="button"
              onClick={refresh}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                border: '1px solid var(--border-color)',
                background: 'var(--bg-card)',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
              title="به‌روزرسانی"
            >
              <i className="fas fa-sync-alt" style={{ fontSize: '0.75rem' }}></i>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="stats-scroll">
          <div className="stats-scroll-inner">
            <StatWidget icon="fa-user-graduate" value={faNum(students.length)} label="دانش‌آموز" color="primary" />
            <StatWidget icon="fa-chalkboard-teacher" value={faNum(teachers.length)} label="معلم" color="success" />
            <StatWidget icon="fa-user-friends" value={faNum(parents.length)} label="والدین" color="warning" />
            <StatWidget icon="fa-chalkboard" value={faNum(teams.length)} label="کلاس" color="info" />
            <StatWidget icon="fa-book-open" value={faNum(uploads.length)} label="محتوا" color="purple" />
            <StatWidget icon="fa-file-alt" value={faNum(exams.length)} label="آزمون" color="danger" />
          </div>
        </div>

        {/* Chart (full width) */}
        <div className="row-grid" style={{ gridTemplateColumns: '1fr' }}>
          <RegistrationChart users={users} />
        </div>

        {/* Management + System Status */}
        <div className="row-grid">
          <Card title="مدیریت" badge="سریع" badgeColor="primary" icon="fa-cog">
            <div className="admin-grid">
              {adminItems.map((item, i) => (
                <button
                  key={i}
                  type="button"
                  className="btn-outline btn-sm admin-btn"
                  onClick={item.action}
                  style={{
                    opacity: item.active ? 1 : 0.65,
                    position: 'relative',
                    fontFamily: 'inherit',
                  }}
                >
                  <i className={`fas ${item.icon}`}></i>
                  {item.label}
                  {!item.active && (
                    <span
                      style={{
                        position: 'absolute',
                        top: '-5px',
                        left: '-5px',
                        fontSize: '0.45rem',
                        background: 'var(--warning)',
                        color: '#fff',
                        padding: '1px 5px',
                        borderRadius: '4px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      به‌زودی
                    </span>
                  )}
                </button>
              ))}
            </div>
          </Card>

          <Card title="وضعیت سیستم" badge="سلامت" badgeColor="success" icon="fa-shield-alt">
            <ServiceStatus name="سرور اصلی" ok={!error} />
            <ServiceStatus name="پایگاه داده" ok={!error} />
            <ServiceStatus name="سرویس API" ok={!error} />
            <div className="system-stats">
              <span>
                <i className="fas fa-clock"></i> به‌روزرسانی: {lastUpdateStr}
              </span>
              <span>
                <i className="fas fa-database"></i> {faNum(users.length)} کاربر
              </span>
            </div>
          </Card>
        </div>
      </div>

      {/* Users Modal */}
      <UsersModal
        isOpen={isUsersModalOpen}
        onClose={() => setIsUsersModalOpen(false)}
        students={students}
        teachers={teachers}
        parents={parents}
      />

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: '90px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--text-primary)',
            color: 'var(--bg-card)',
            padding: '10px 22px',
            borderRadius: '30px',
            fontSize: '0.8rem',
            fontWeight: 600,
            zIndex: 999999,
            boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
            whiteSpace: 'nowrap',
          }}
        >
          {toast}
        </div>
      )}
    </>
  );
};

export default AdminHome;